import { first, last, cloneDeep, clone } from 'lodash';
import { cloneSubtitle, removeEmptyLines } from '../../common/functions';

// ***************************************************************************************
// onTakeWordToSubtitle permet de prendre
// le mot du sous-titre (précédent/courant/suivant) vers le sous-titre (précédent, courant, suivant)
// ***************************************************************************************
export function onTakeWordToSubtitle(key, shiftKeyPressed) {
  switch (key) {
    case 'TAKEWORDFROMLASTSUBTITLETOCURRENTSUBTITLE':
      this.onTakeWordFromLastSubtitleToCurrentSubtitle();
      break;
    case 'TAKEWORDFROMNEXTSUBTITLETOCURRENTSUBTITLE':
      this.onTakeWordFromNextSubtitleToCurrentSubtitle();
      break;
    case 'TAKEWORDFROMCURRENTSUBTITLETOPREVSUBTITLE':
      this.onTakeWordFromCurrentSubtitleToPrevSubtitle(shiftKeyPressed);
      break;
    case 'TAKEWORDFROMCURRENTSUBTITLETONEXTSUBTITLE':
      this.onTakeWordFromCurrentSubtitleToNextSubtitle(shiftKeyPressed);
      break;
    default:
      return;
  }
}

// ***************************************************************************************
// onTakeWordFromLastSubtitleToCurrentSubtitle permet de prendre
// le dernier mot du sous-titre précédent et le mettre au début du sous-titre en cours
// ***************************************************************************************
export function onTakeWordFromLastSubtitleToCurrentSubtitle() {
  const currentSubtitle = this.subtitle;
  const prevSubtitle = this.props.subtitles[currentSubtitle.id - 1];

  const oldSubtitles = cloneDeep(this.props.subtitles);
  const oldCurrentSubtitleId = clone(this.subtitle.id);

  if (!currentSubtitle.lines.length) {
    return;
  }

  if (!prevSubtitle || !prevSubtitle.lines.length) {
    return;
  }

  const lastLinePrevSubtitle = last(prevSubtitle.lines);
  if (!lastLinePrevSubtitle || !lastLinePrevSubtitle.words.length) {
    return;
  }

  const lastWordPrevSubtitle = last(lastLinePrevSubtitle.words);

  const firstLineCurrentSubtitle = first(currentSubtitle.lines);

  // supprimer le mot du sous-titre précédent
  lastLinePrevSubtitle.words.pop();

  // ajouter le mot dans le sous-titre courant
  firstLineCurrentSubtitle.words.unshift(lastWordPrevSubtitle);

  const nbChars =
    this.getSentence(firstLineCurrentSubtitle).length +
    lastWordPrevSubtitle.letters.length;
  const maxLength = this.getMaxLength(firstLineCurrentSubtitle);

  // Si on dépasse le nb de caractères dans la ligne, alors il faut tout décaler dans les sous-titres suivants
  if (nbChars > maxLength) {
    const success = this.shiftWordsToNext(
      currentSubtitle,
      maxLength,
      firstLineCurrentSubtitle,
      lastLinePrevSubtitle,
      lastWordPrevSubtitle
    );

    if (!success) {
      //this.cancelUndo();
      return;
    }
  }

  // s'il n'y a plus de mots, on supprime la ligne
  if (!lastLinePrevSubtitle.words.length && prevSubtitle.lines.length > 1) {
    prevSubtitle.lines.pop();
  }

  // Faire l'historique undo/redo
  this.addSubtitlesToUndoOrRedo(
    'onTakeWordFromLastSubtitleToCurrentSubtitle',
    oldSubtitles,
    oldCurrentSubtitleId,
    this.props.subtitles,
    currentSubtitle.id
  );

  const prevSubtitleCloned = cloneSubtitle(prevSubtitle);
  const currentSubtitleClone = cloneSubtitle(currentSubtitle);
  this.props.onUpdateSubtitles(
    [prevSubtitleCloned, currentSubtitleClone],
    currentSubtitle.id
  );
}

// ***************************************************************************************
// onTakeWordFromNextSubtitleToCurrentSubtitle permet de prendre
// le premier mot du sous-titre suivant et le mettre à la fin du sous-titre en cours.
// ***************************************************************************************
export function onTakeWordFromNextSubtitleToCurrentSubtitle() {
  const currentSubtitle = this.subtitle;
  const nextSubtitle = this.props.subtitles[currentSubtitle.id + 1];

  const oldSubtitles = cloneDeep(this.props.subtitles);
  const oldCurrentSubtitleId = clone(this.subtitle.id);

  if (!currentSubtitle.lines.length) {
    return;
  }

  if (!nextSubtitle || !nextSubtitle.lines.length) {
    return;
  }

  const firstLineNextSubtitle = first(nextSubtitle.lines);
  if (!firstLineNextSubtitle || !firstLineNextSubtitle.words.length) {
    return;
  }

  const firstWordNextSubtitle = first(firstLineNextSubtitle.words);

  const lastLineCurrentSubtitle = last(currentSubtitle.lines);

  // supprimer le mot du sous-titre suivant
  firstLineNextSubtitle.words.shift();

  // ajouter le mot dans le sous-titre courant
  lastLineCurrentSubtitle.words.push(firstWordNextSubtitle);

  const nbChars =
    this.getSentence(lastLineCurrentSubtitle).length +
    firstWordNextSubtitle.letters.length;
  const maxLength = this.getMaxLength(lastLineCurrentSubtitle);

  // Si on dépasse le nb de caractères dans la ligne, alors il faut tout décaler
  if (nbChars > maxLength) {
    const success = this.shiftWordsToPrev(
      currentSubtitle,
      maxLength,
      firstLineNextSubtitle,
      lastLineCurrentSubtitle,
      firstWordNextSubtitle
    );

    if (!success) {
      // this.cancelUndo();
      return;
    }
  }

  // s'il n'y a plus de mots, on supprime la ligne
  if (!firstLineNextSubtitle.words.length && nextSubtitle.lines.length > 1) {
    nextSubtitle.lines.shift();
  }

  // Faire l'historique undo/redo
  this.addSubtitlesToUndoOrRedo(
    'onTakeWordFromNextSubtitleToCurrentSubtitle',
    oldSubtitles,
    oldCurrentSubtitleId,
    this.props.subtitles,
    currentSubtitle.id
  );

  const nextSubtitleCloned = cloneSubtitle(nextSubtitle);
  const currentSubtitleClone = cloneSubtitle(currentSubtitle);
  this.props.onUpdateSubtitles(
    [currentSubtitleClone, nextSubtitleCloned],
    currentSubtitle.id
  );
}

// ***************************************************************************************
// onTakeWordFromCurrentSubtitleToPrevSubtitle permet de prendre
// le premier mot du sous-titre en cours et le mettre à la fin du sous-titre précédent.
// ***************************************************************************************
export function onTakeWordFromCurrentSubtitleToPrevSubtitle(shiftKeyPressed, takeCurrentSubtitleId = true) {
  const currentSubtitle = this.subtitle;
  const prevSubtitle = this.props.subtitles[currentSubtitle.id - 1];

  const oldSubtitles = cloneDeep(this.props.subtitles);
  const oldCurrentSubtitleId = clone(this.subtitle.id);

  if (!currentSubtitle.lines.length) {
    return;
  }

  if (!prevSubtitle || !prevSubtitle.lines.length) {
    return;
  }

  const firstLineCurrentSubtitle = first(currentSubtitle.lines);
  if (!firstLineCurrentSubtitle || !firstLineCurrentSubtitle.words.length) {
    return;
  }

  const firstWordCurrentSubtitle = first(firstLineCurrentSubtitle.words);

  // ajouter le mot au sous-titre précédent
  const lastLinePrevSubtitle = last(prevSubtitle.lines);
  lastLinePrevSubtitle.words.push(firstWordCurrentSubtitle);

  // supprimer le mot du sous-titre courant
  firstLineCurrentSubtitle.words.shift();

  const nbChars =
    this.getSentence(lastLinePrevSubtitle).length +
    (firstWordCurrentSubtitle.letters
      ? firstWordCurrentSubtitle.letters.length
      : firstWordCurrentSubtitle.text.length);
  const maxLength = this.getMaxLength(lastLinePrevSubtitle);

  // Si on dépasse le nb de caractères dans la ligne, alors il faut tout décaler
  if (nbChars > maxLength) {
    const success = this.shiftWordsToPrev(
      prevSubtitle,
      maxLength,
      firstLineCurrentSubtitle,
      lastLinePrevSubtitle,
      firstWordCurrentSubtitle
    );

    if (!success) {
      // this.cancelUndo();
      return;
    }
  }

  // s'il n'y a plus de mots, on supprime la ligne
  if (
    !firstLineCurrentSubtitle.words.length &&
    currentSubtitle.lines.length > 1
  ) {
    currentSubtitle.lines.shift();
  }

  if (shiftKeyPressed) {
    const wordsPreviousSubtitle = this.getWordsOfSubtitle(prevSubtitle);
    const lastWord = last(wordsPreviousSubtitle);

    const { interst } = this.props.subtitleParameters;
    

    if (lastWord.out) {   
      
      const outTime = lastWord.out;
      const inTime = outTime + interst;

      if (prevSubtitle.in < outTime && inTime < currentSubtitle.out) {
        prevSubtitle.out = outTime;
        currentSubtitle.in = inTime;
      }
    }
  }

  // Faire l'historique undo/redo
  this.addSubtitlesToUndoOrRedo(
    'onTakeWordFromCurrentSubtitleToPrevSubtitle',
    oldSubtitles,
    oldCurrentSubtitleId,
    this.props.subtitles,
    currentSubtitle.id
  );

  const prevSubtitleCloned = cloneSubtitle(prevSubtitle);
  const currentSubtitleClone = cloneSubtitle(currentSubtitle);
  this.props.onUpdateSubtitles(
    [prevSubtitleCloned, currentSubtitleClone],
    takeCurrentSubtitleId ? currentSubtitle.id : undefined
  );
}

// ***************************************************************************************
// onTakeWordFromCurrentSubtitleToNextSubtitle permet de prendre
//  le dernier mot du sous-titre en cours et le mettre au début du sous-titre suivant.
// ***************************************************************************************
export function onTakeWordFromCurrentSubtitleToNextSubtitle(shiftKeyPressed, takeCurrentSubtitleId = true) {
  const currentSubtitle = this.subtitle;
  const nextSubtitle = this.props.subtitles[currentSubtitle.id + 1];

  const oldSubtitles = cloneDeep(this.props.subtitles);
  const oldCurrentSubtitleId = clone(this.subtitle.id);

  if (!currentSubtitle.lines.length) {
    return;
  }

  if (!nextSubtitle || !nextSubtitle.lines.length) {
    return;
  }

  const lastLineCurrentSubtitle = last(currentSubtitle.lines);
  if (!lastLineCurrentSubtitle || !lastLineCurrentSubtitle.words.length) {
    return;
  }

  const lastWordCurrentSubtitle = last(lastLineCurrentSubtitle.words);

  // ajouter le mot au sous-titre suivant
  let firstLineNextSubtitle = first(nextSubtitle.lines);
  firstLineNextSubtitle.words.unshift(lastWordCurrentSubtitle);

  // supprimer le mot du sous-titre courant
  lastLineCurrentSubtitle.words.pop();

  const nbChars =
    this.getSentence(firstLineNextSubtitle).length +
    (lastWordCurrentSubtitle.letters
      ? lastWordCurrentSubtitle.letters.length
      : lastWordCurrentSubtitle.text.length);
  const maxLength = this.getMaxLength(firstLineNextSubtitle);

  // Si on dépasse le nb de caractères dans la ligne, alors il faut tout décaler dans les sous-titres suivants
  if (nbChars > maxLength) {
    const success = this.shiftWordsToNext(
      nextSubtitle,
      maxLength,
      firstLineNextSubtitle,
      lastLineCurrentSubtitle,
      lastWordCurrentSubtitle
    );

    if (!success) {
      //  this.cancelUndo();
      return;
    }

    
  }

  // s'il n'y a plus de mots, on supprime la ligne
  if (
    !lastLineCurrentSubtitle.words.length &&
    currentSubtitle.lines.length > 1
  ) {
    currentSubtitle.lines.pop();
  }

    
  if (shiftKeyPressed) {
    const wordsNextSubtitle = this.getWordsOfSubtitle(nextSubtitle);
    const firstWord = first(wordsNextSubtitle);

    const { interst } = this.props.subtitleParameters;

    if (firstWord.in) {    

      const inTime = firstWord.in;
      const outTime = inTime - interst;

      if (currentSubtitle.in < outTime && inTime < nextSubtitle.out) {
          currentSubtitle.out = outTime;
          nextSubtitle.in = inTime;    
      }
    }
  }

  // Faire l'historique undo/redo
  this.addSubtitlesToUndoOrRedo(
    'onTakeWordFromCurrentSubtitleToPrevSubtitle',
    oldSubtitles,
    oldCurrentSubtitleId,
    this.props.subtitles,
    currentSubtitle.id
  );

  const nextSubtitleCloned = cloneSubtitle(nextSubtitle);
  const currentSubtitleClone = cloneSubtitle(currentSubtitle);
  this.props.onUpdateSubtitles(
    [currentSubtitleClone, nextSubtitleCloned],
    takeCurrentSubtitleId ? currentSubtitle.id : undefined
  );
}

// ***************************************************************************************
// onCutSubtitle permet de couper un sous-titre en deux
// ***************************************************************************************
export function onCutSubtitle() {
  if (!this.currentWord) {
    return;
  }

  const currentSubtitle = this.subtitle;

  const oldSubtitles = cloneDeep(this.props.subtitles);
  const oldCurrentSubtitleId = clone(this.subtitle.id);

  const currentLineIndex = this.currentLineIndex;
  const currentWordIndex = this.currentWordIndex;
  const currentNbChars = currentSubtitle.len;

  // Clone current subtitle to avoid mutating props directly
  const newSubtitle = cloneDeep(currentSubtitle);

  // Find the line and word where to split
  const lineToSplit = newSubtitle.lines[currentLineIndex];
  if (!lineToSplit) return;

  const wordsBefore = lineToSplit.words.slice(0, currentWordIndex);
  const wordsAfter = lineToSplit.words.slice(currentWordIndex);

  const wordToSplit = wordsAfter[0];
  if (wordToSplit) {
    const lastLetter = last(wordToSplit.letters);
    if (
      this.currentLetterIndex > 0 &&
      this.currentLetterIndex <= lastLetter.indexLetter
    ) {
      const beforeText = wordToSplit.text.slice(0, this.currentLetter.index);
      const afterText = wordToSplit.text.slice(this.currentLetter.index);

      // Create two new word objects, preserving properties
      const beforeWord = { ...wordToSplit, text: beforeText };
      const afterWord = { ...wordToSplit, text: afterText };

      // Replace the first word in wordsAfter with the two split words
      wordsBefore.push(beforeWord);
      wordsAfter.splice(0, 1, afterWord);
    }
  }

  // If nothing to split, do nothing
  if (!wordsAfter.length) return;

  // Create two new lines for the split
  const firstPartLines = newSubtitle.lines.slice(0, currentLineIndex);
  const secondPartLines = newSubtitle.lines.slice(currentLineIndex + 1);

  const firstSubtitleLines = [
    ...firstPartLines,
    { ...lineToSplit, words: wordsBefore },
  ];

  const secondSubtitleLines = [
    { ...lineToSplit, words: wordsAfter },
    ...secondPartLines,
  ];

  // Prepare the new subtitles
  const newSubtitles = [...this.props.subtitles.map((x) => cloneSubtitle(x))];
  const currentSubtitleIndex = currentSubtitle.id;

  // Create the new subtitle object
  const splittedSubtitle = {
    ...cloneSubtitle(currentSubtitle),
    lines: secondSubtitleLines,
  };

  // Update the current subtitle with the first part
  const newCurrentSubtitle = {
    ...cloneSubtitle(currentSubtitle),
    lines: firstSubtitleLines,
  };

  // Update timecodes
  const { interst } = this.props.subtitleParameters;

  const newCurrentNbChars = this.getNbCharsOfSubtitle(newCurrentSubtitle);
  const duration = newCurrentSubtitle.out - newCurrentSubtitle.in;

  const newOutTime =
    newCurrentSubtitle.in +
    (newCurrentNbChars / currentNbChars) *
      (Number.parseFloat(duration) - interst);

  newCurrentSubtitle.out = newOutTime;
  splittedSubtitle.in = newOutTime + interst;

  newSubtitles[currentSubtitleIndex] = newCurrentSubtitle;

  // Insert the new subtitle after the current one
  newSubtitles.splice(currentSubtitleIndex + 1, 0, splittedSubtitle);

  // new indexes
  newSubtitles.forEach((subtitle, index) => {
    subtitle.id = index;
    subtitle.len = this.getNbChars();
    subtitle.lines.forEach((line) => {
      line.len = this.getSentence(line).length;
    });
  });

  // Faire l'historique undo/redo
  this.addSubtitlesToUndoOrRedo(
    'onCutSubtitles',
    oldSubtitles,
    oldCurrentSubtitleId,
    newSubtitles,
    currentSubtitle.id
  );

  // Call the update callback
  this.props.onUpdateSubtitles(newSubtitles, currentSubtitle.id, true);
}

// ***************************************************************************************
// onRegroupSubtitle permet de regrouper le sous-titre en cours avec le suivant
// ***************************************************************************************
export function onRegroupSubtitle() {
  alert ("regoupe")
  const { subtitles } = this.props;

  const oldSubtitles = cloneDeep(subtitles);
  const oldCurrentSubtitleId = clone(this.subtitle.id);

  const currentSubtitle = this.subtitle;
  const nextSubtitle = cloneDeep(subtitles[currentSubtitle.id + 1]);

  if (!nextSubtitle) {
    return;
  }

  let success = true;

  removeEmptyLines(currentSubtitle);
  removeEmptyLines(nextSubtitle);

  if (currentSubtitle.lines.length === 1 && nextSubtitle.lines.length === 1) {
    const { displayNumberOfLines, teletext } =
      this.props.subtitleParameters;

    
    const currentLine = currentSubtitle.lines[0];
    const nextLine = nextSubtitle.lines[0];
    console.log("line ", currentLine)


    let vPos = currentLine.verticalPos;
    const doubleHeight = !teletext?false:currentLine.doubleHeight;

    const nextPos = doubleHeight ? 2 : 1;

    if (vPos + nextPos >= displayNumberOfLines) {
      this.onMoveSubtitleToUp(currentLine);
      if (teletext) this.onMoveSubtitleToUp(currentLine);
    }

    nextLine.verticalPos = currentLine.verticalPos + nextPos;
    currentSubtitle.lines.push(nextLine);
  } else {
    nextSubtitle.lines.forEach((line) => {
      if (!success) {
        return;
      }

      line.words.forEach((word) => {
        const lastLine = last(currentSubtitle.lines);

        lastLine.words.push(word);

        const sentence = this.getSentence(lastLine);
        const maxLength = this.getMaxLength(lastLine);

        if (sentence.length > maxLength) {
          success = this.shiftWordsToPrev(
            currentSubtitle,
            maxLength,
            line,
            lastLine,
            word
          );

          if (!success) {
            return;
          }
        }
      });
    });

    if (!success) {
      // POPUP

      this.props.onShowPopup({
        title: '',
        text: {
          fr: 'Vous ne pouvez pas regrouper les deux sous-titres car, avec le sous-titre suivant, on va dépasser la limite du nombre de caractères autorisé.',
          en: "You can't group the two subtitles together, because the following subtitle will exceed the maximum number of characters allowed.",
        },
        buttons: [],
        showIcon: true,
        icon: null,
      });

      this.props.onUpdateSubtitles(oldSubtitles, currentSubtitle.id);

      return;
    }
  }

  // Mettre à jour le time code
  currentSubtitle.out = nextSubtitle.out;



  // Supprimer le sous-titre
  subtitles.splice(nextSubtitle.id, 1);
  subtitles[currentSubtitle.id] = currentSubtitle;

  subtitles.forEach((subtitle, index) => {
    subtitle.id = index;
    subtitle.len = this.getNbChars();
    subtitle.lines.forEach((line) => {
      line.len = this.getSentence(line).length;
    });
  });

  this.addSubtitlesToUndoOrRedo(
    'OnRegroupSubtitles',
    oldSubtitles,
    oldCurrentSubtitleId,
    subtitles,
    currentSubtitle.id
  );

  this.props.onUpdateSubtitles(
    subtitles.map((x) => cloneSubtitle(x)),
    currentSubtitle.id,
    true
  );
}

export function onMoveWordToPreviousSubtitle(shiftKeyPressed) {
  this.onTakeWordFromCurrentSubtitleToPrevSubtitle(shiftKeyPressed, this.state.active);
}

export function onMoveWordToNextSubtitle(shiftKeyPressed) {
  this.onTakeWordFromCurrentSubtitleToNextSubtitle(shiftKeyPressed, this.state.active);
}

export function onMoveWordToPreviousLine() {

  if (this.subtitle.lines.length <= 1) {
    return;
  }

  const firstLine = this.subtitle.lines[0];
  const secondLine = this.subtitle.lines[1];

  if (!secondLine.words.length) {
    return;
  }

  const firstWord = first(secondLine.words);

  const nbChars = this.getSentence(firstLine).length + firstWord.letters.length;
  const maxLength = this.getMaxLength(firstLine);

  // Si on dépasse le nb de caractères dans la ligne
  if (nbChars > maxLength) {
   /* this.props.onShowPopup({
        title: '',
        text: {
          fr: 'Vous ne pouvez pas mettre le mot à la ligne précédente car on va dépasser la limite du nombre de caractères autorisé.',
          en: "You can't move the word to the previous line because that would exceed the character limit.",
        },
        buttons: [],
        showIcon: true,
        icon: null,
      });*/
    return;
  }

  this.addNewActionForUndo('onMoveWordToPreviousLine');

  secondLine.words.shift();
  firstLine.words.push(firstWord);

  this.onChange();
}

export function onMoveWordToNextLine() {

  if (this.subtitle.lines.length <= 1) {
    return;
  }

  const firstLine = this.subtitle.lines[0];
  const secondLine = this.subtitle.lines[1];

  if (!firstLine.words.length) {
    return;
  }

  const lastWord = last(firstLine.words);

  const nbChars = this.getSentence(secondLine).length + lastWord.letters.length;
  const maxLength = this.getMaxLength(secondLine);

  // Si on dépasse le nb de caractères dans la ligne
  if (nbChars > maxLength) {
   /* this.props.onShowPopup({
        title: '',
        text: {
          fr: 'Vous ne pouvez pas mettre le mot à la ligne suivante car on va dépasser la limite du nombre de caractères autorisé.',
          en: "You can't move the word to the next line because that would exceed the character limit.",
        },
        buttons: [],
        showIcon: true,
        icon: null,
      });*/
    return;
  }

  
  this.addNewActionForUndo('onMoveWordToNextLine');

  firstLine.words.pop();
  secondLine.words.unshift(lastWord);

  this.onChange();
}